import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Send, Upload, X, Users, Mail, CheckSquare, Square } from 'lucide-react';

export default function CorreoMasivoPage() {
  const [segmentos, setSegmentos] = useState({});
  const [loading, setLoading] = useState(true);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [filtroSector, setFiltroSector] = useState('todos');
  const [form, setForm] = useState({ asunto: '', cuerpo: '' });
  const [imagen, setImagen] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const imgRef = useRef();

  useEffect(() => {
    api.get('/email/segmentos').then(r => {
      setSegmentos(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const todosDestinatarios = Object.values(segmentos).flat();
  const destinatariosFiltrados = filtroSector === 'todos'
    ? todosDestinatarios
    : segmentos[filtroSector] || [];

  const toggleDest = (email) => {
    const ns = new Set(seleccionados);
    if (ns.has(email)) ns.delete(email); else ns.add(email);
    setSeleccionados(ns);
  };

  const selectAll = () => {
    const emails = destinatariosFiltrados.map(d => d.email);
    const allSel = emails.every(e => seleccionados.has(e));
    const ns = new Set(seleccionados);
    if (allSel) emails.forEach(e => ns.delete(e));
    else emails.forEach(e => ns.add(e));
    setSeleccionados(ns);
  };

  const selectBySector = (sector) => {
    const emails = (segmentos[sector] || []).map(d => d.email);
    const ns = new Set(seleccionados);
    emails.forEach(e => ns.add(e));
    setSeleccionados(ns);
  };

  const handleImagen = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error('Imagen máx. 3MB'); return; }
    const reader = new FileReader();
    reader.onload = ev => setImagen({ data: ev.target.result, tipo: file.type, nombre: file.name });
    reader.readAsDataURL(file);
  };

  const handleEnviar = async () => {
    if (!form.asunto) { toast.error('Escribe el asunto'); return; }
    if (!form.cuerpo) { toast.error('Escribe el cuerpo del correo'); return; }
    if (seleccionados.size === 0) { toast.error('Selecciona al menos un destinatario'); return; }
    if (!window.confirm(`¿Enviar correo a ${seleccionados.size} persona(s)?`)) return;

    setEnviando(true); setResultado(null);
    try {
      const destArray = todosDestinatarios.filter(d => seleccionados.has(d.email));
      const res = await api.post('/email/enviar-masivo', {
        asunto: form.asunto,
        cuerpo: form.cuerpo,
        destinatarios: destArray,
        imagenBase64: imagen?.data,
        imagenTipo: imagen?.tipo,
      });
      setResultado(res.data);
      toast.success(res.data.message);
    } catch(e) {
      toast.error(e.response?.data?.error || 'Error al enviar');
    } finally { setEnviando(false); }
  };

  const sectores = Object.keys(segmentos);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Correo masivo</h1>
          <p className="page-subtitle">Envía correos segmentados a tus clientes</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20, alignItems: 'start' }}>

        {/* Destinatarios */}
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 14 }}>Destinatarios</h3>

          {/* Filtro por sector */}
          <div style={{ marginBottom: 12 }}>
            <label className="form-label">Filtrar por sector</label>
            <select className="form-select" value={filtroSector} onChange={e => setFiltroSector(e.target.value)}>
              <option value="todos">Todos los sectores ({todosDestinatarios.length})</option>
              {sectores.map(s => (
                <option key={s} value={s}>{s} ({(segmentos[s] || []).length})</option>
              ))}
            </select>
          </div>

          {/* Botones rápidos por sector */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {sectores.map(s => (
              <button key={s} onClick={() => selectBySector(s)}
                style={{ padding: '4px 10px', borderRadius: 20, border: '1px solid #e5e5e5', background: '#fafafa', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
                + {s}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <button onClick={selectAll} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#7B0D0D', fontWeight: 600 }}>
              {destinatariosFiltrados.every(d => seleccionados.has(d.email)) ? <CheckSquare size={16}/> : <Square size={16}/>}
              Seleccionar todos
            </button>
            <span style={{ fontSize: 12, color: '#888' }}>{seleccionados.size} seleccionados</span>
          </div>

          <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {loading ? <div style={{ textAlign: 'center', padding: 20, color: '#888' }}>Cargando...</div>
            : destinatariosFiltrados.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#aaa', fontSize: 13 }}>
                Sin contactos con email en este sector
              </div>
            ) : destinatariosFiltrados.map(d => (
              <label key={d.email} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: seleccionados.has(d.email) ? '#fff5f5' : 'transparent', border: seleccionados.has(d.email) ? '1px solid #fecaca' : '1px solid transparent' }}>
                <input type="checkbox" checked={seleccionados.has(d.email)} onChange={() => toggleDest(d.email)} style={{ accentColor: '#7B0D0D', width: 15, height: 15 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nombre}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{d.empresa}</div>
                  <div style={{ fontSize: 11, color: '#7B0D0D' }}>{d.email}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Redactar correo */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: 16 }}>Redactar correo</h3>

            <div className="form-group">
              <label className="form-label">Asunto *</label>
              <input className="form-input" value={form.asunto} onChange={e => setForm(p => ({ ...p, asunto: e.target.value }))} placeholder="Ej: Oferta especial para el sector petroquímico" />
            </div>

            {/* Imagen */}
            <div className="form-group">
              <label className="form-label">Imagen (opcional)</label>
              {imagen ? (
                <div style={{ padding: 12, background: '#f9f9f9', borderRadius: 8, border: '1px solid #e5e5e5', display: 'flex', gap: 12, alignItems: 'center' }}>
                  <img src={imagen.data} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{imagen.nombre}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>Se mostrará en el correo</div>
                  </div>
                  <button onClick={() => setImagen(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}><X size={16}/></button>
                </div>
              ) : (
                <div onClick={() => imgRef.current.click()} style={{ border: '2px dashed #ccc', borderRadius: 8, padding: 20, textAlign: 'center', cursor: 'pointer', background: '#fafafa' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#7B0D0D'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#ccc'}>
                  <Upload size={24} style={{ color: '#ccc', marginBottom: 6 }} />
                  <p style={{ color: '#888', fontSize: 13 }}>Clic para agregar imagen</p>
                  <p style={{ color: '#aaa', fontSize: 11 }}>PNG, JPG — máx. 3MB</p>
                </div>
              )}
              <input ref={imgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImagen} />
            </div>

            <div className="form-group">
              <label className="form-label">Cuerpo del correo *</label>
              <textarea className="form-textarea" rows={10} value={form.cuerpo}
                onChange={e => setForm(p => ({ ...p, cuerpo: e.target.value }))}
                placeholder="Estimado cliente,&#10;&#10;Nos complace informarle sobre nuestros productos y servicios...&#10;&#10;Quedamos a su disposición para cualquier consulta." />
              <span style={{ fontSize: 11, color: '#888', marginTop: 4, display: 'block' }}>
                El correo llegará con la firma de tu perfil y con copia (CC) a tu correo
              </span>
            </div>

            <button className="btn btn-primary" onClick={handleEnviar} disabled={enviando || seleccionados.size === 0}
              style={{ width: '100%', justifyContent: 'center' }}>
              <Send size={16} />
              {enviando ? 'Enviando...' : `Enviar a ${seleccionados.size} destinatario(s)`}
            </button>
          </div>

          {/* Preview */}
          {(form.asunto || form.cuerpo) && (
            <div className="card">
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: '#555' }}>Vista previa del correo</h4>
              <div style={{ border: '1px solid #e5e5e5', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ background: '#7B0D0D', padding: '16px 20px', textAlign: 'center' }}>
                  <div style={{ color: 'white', fontSize: 16, fontWeight: 900, letterSpacing: 2 }}>ADRANTALEH S.A.S.</div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 9, marginTop: 2 }}>PLENTY OF INDUSTRIAL SOLUTIONS IN ONLY ONE COMPANY</div>
                </div>
                <div style={{ padding: '16px 20px', background: 'white' }}>
                  {form.asunto && <div style={{ fontWeight: 700, marginBottom: 12, color: '#333' }}>{form.asunto}</div>}
                  {imagen && <img src={imagen.data} alt="" style={{ maxWidth: '100%', borderRadius: 6, marginBottom: 12 }} />}
                  <div style={{ fontSize: 13, lineHeight: 1.7, color: '#444', whiteSpace: 'pre-wrap' }}>{form.cuerpo}</div>
                </div>
                <div style={{ background: '#1a1a1a', padding: '10px 20px', textAlign: 'center' }}>
                  <div style={{ color: '#888', fontSize: 9, letterSpacing: 2 }}>aspen aerogels · CHESTERTON · CERAMIC POLYMER · TEMP-COAT</div>
                </div>
              </div>
            </div>
          )}

          {/* Resultado */}
          {resultado && (
            <div style={{ padding: '14px 18px', background: resultado.fallidos?.length > 0 ? '#fffbeb' : '#f0fdf4', borderRadius: 10, border: `1px solid ${resultado.fallidos?.length > 0 ? '#fef3c7' : '#bbf7d0'}` }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>{resultado.message}</div>
              {resultado.fallidos?.length > 0 && (
                <div style={{ fontSize: 13, color: '#dc2626' }}>
                  Fallidos: {resultado.fallidos.map(f => f.email).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
